/**
 * AddDeviceAtMapModal — Sprint 4.1.1.
 *
 * Modal aberto por clique direito no mapa. Permite escolher um device
 * (entre os SEM geo cadastrada) e definir nome do local. Chama
 * POST /api/admin/devices/{id}/geo com lat/lng do clique.
 *
 * Closeable via ✕, Esc, ou clique fora.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { setDeviceGeo } from "../../lib/api.js";
import { CAP, hasCapability } from "../../lib/rbac.js";
import { useCriticalConfirm } from "../ConfirmCriticalAction.jsx";
import { getProtectionState } from "../../lib/protection.js";

export default function AddDeviceAtMapModal({
  open,
  lat,
  lng,
  candidates,    // devices sem geo
  onClose,
  onSaved,
}) {
  const [deviceId, setDeviceId] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const confirmCritical = useCriticalConfirm();

  // MAP-MANAGE-1E-B — device selecionado + estado de proteção (vem do DTO do
  // fleet-map/devices via MapView, MAP-DATA-1B; backend é a fonte da verdade).
  const selectedDev = (candidates || []).find((d) => d.device_id === deviceId);
  const protectedDev = getProtectionState(selectedDev).protected;

  useEffect(() => {
    if (open) {
      setDeviceId("");
      setName("");
      setAddress("");
      setFilter("");
      setError(null);
      setSaving(false);
    }
  }, [open, lat, lng]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const filtered = candidates.filter((d) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      d.device_id.toLowerCase().includes(q)
      || (d.customer_code || "").toLowerCase().includes(q)
      || (d.customer_name || "").toLowerCase().includes(q)
    );
  });

  const handleSave = async () => {
    if (!deviceId) {
      setError("Selecione um device.");
      return;
    }
    // MAP-MANAGE-1E-B — gate de capability (backend reconfirma a verdade).
    if (!hasCapability(CAP.FLEET_WRITE)) {
      setError("Seu usuário não tem permissão para alterar localização (fleet:write).");
      return;
    }
    let confirm;
    if (protectedDev) {
      // Device protegido (produção): exige confirmação forte.
      const secret = await confirmCritical({
        actionLabel: "Salvar localização de device PROTEGIDO (produção)",
        target: { kind: "device", id: deviceId },
        impact: "Device protegido: alterar a localização exige confirmação forte. "
          + "Isso altera apenas o pin no NOC.",
      });
      if (secret == null) return;  // cancelou
      confirm = secret;
    }
    setSaving(true);
    setError(null);
    try {
      await setDeviceGeo(
        deviceId,
        { lat, lng, name: name || null, address: address || null },
        { confirm },
      );
      onSaved();
    } catch (e) {
      const why = e?.status === 403
        ? `403 — ${e?.message || "permissão ou confirmação forte necessária"}`
        : (e?.message || "Erro ao salvar geolocalização");
      setError(why);
      setSaving(false);
    }
  };

  // Robustez/simetria (MAP-UX): portaliza p/ document.body como
  // ConfirmCriticalAction/BulkRelayRebootModal — imune a transform/filter futuro
  // em .ipwd-fleet-focus (mapa fullscreen). z-[2000] segue acima do mapa e abaixo
  // da confirmação crítica (z-3000/4000).
  return createPortal(
    <div
      className="fixed inset-0 z-[2000] bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-noc-panel border border-noc-border rounded shadow-2xl w-full max-w-md p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">📍 Adicionar device neste ponto</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-noc-muted hover:text-noc-text text-lg leading-none"
          >×</button>
        </div>

        <div className="text-[10px] text-noc-muted font-mono">
          lat: {lat?.toFixed(6)}, lng: {lng?.toFixed(6)}
        </div>

        {candidates.length === 0 ? (
          <div className="text-xs text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
            Todos os devices já têm localização cadastrada. Use "✥ Mover" no
            popup de um marker se quiser reposicioná-lo.
          </div>
        ) : (
          <>
            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Buscar device</span>
              <input
                placeholder="device_id, customer, etc"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1"
              />
            </label>

            <div className="max-h-48 overflow-y-auto space-y-0.5 border border-noc-border rounded p-1">
              {filtered.map((d) => (
                <button
                  key={d.device_id}
                  type="button"
                  onClick={() => setDeviceId(d.device_id)}
                  className={`w-full text-left px-2 py-1 rounded text-[11px] flex items-center gap-2 ${
                    deviceId === d.device_id
                      ? "bg-noc-ok/10 border border-noc-ok/30"
                      : "hover:bg-noc-bg/50"
                  }`}
                >
                  <span className="font-mono flex-1">{d.device_id}</span>
                  {d.customer_code && (
                    <span className="text-noc-muted text-[9px]">{d.customer_code}</span>
                  )}
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="text-[10px] text-noc-muted py-2 text-center">
                  Nenhum device com esse filtro.
                </div>
              )}
            </div>

            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Nome do local (opcional)</span>
              <input
                placeholder={`LAB-${deviceId || "DEVICE"}`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1"
              />
            </label>

            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Endereço (opcional)</span>
              <input
                placeholder="Rua, número, bairro"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1"
              />
            </label>

            {protectedDev && (
              <div className="text-xs text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
                🔒 Device protegido (produção): alteração de localização exige
                confirmação forte.
              </div>
            )}
            {error && (
              <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="text-xs px-3 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text"
              >Cancelar</button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !deviceId}
                className="text-xs px-3 py-1 rounded bg-noc-ok hover:brightness-110 text-noc-bg font-semibold disabled:opacity-50"
              >{saving ? "Salvando…" : "Salvar"}</button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
